// src/pages/Dashboard/Admin/Articles/components/ArticlesCatalogTable.jsx

import React from "react";
import {
  FaArrowDown,
  FaArrowUp,
  FaEdit,
  FaImage,
  FaTrash,
} from "react-icons/fa";
import { RxDragHandleDots2 } from "react-icons/rx";

import { toAbsoluteArticlePhotoUrl } from "../../../../../services/articlesPhotoServices";

import {
  ARTICLE_FALLBACK_IMAGE,
  formatEuro,
  isLicenceCotisationArticle,
} from "../utils/articlesDashboardUtils";

function StatusBadge({ active, disabled, onClick }) {
  return (
    <button
      type="button"
      disabled={disabled}
      onClick={onClick}
      className={`inline-flex items-center rounded-full px-3 py-1 text-xs font-bold ring-1 transition disabled:cursor-not-allowed disabled:opacity-60 ${
        active
          ? "bg-emerald-50 text-emerald-700 ring-emerald-200 hover:bg-emerald-100 dark:bg-emerald-500/10 dark:text-emerald-300 dark:ring-emerald-500/20"
          : "bg-slate-100 text-slate-600 ring-slate-200 hover:bg-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700"
      }`}
    >
      {active ? "Affiché" : "Masqué"}
    </button>
  );
}

function BooleanBadge({ value, yes = "Oui", no = "Non" }) {
  return (
    <span
      className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${
        value
          ? "bg-indigo-50 text-indigo-700 ring-indigo-200 dark:bg-indigo-500/10 dark:text-indigo-300 dark:ring-indigo-500/20"
          : "bg-slate-100 text-slate-600 ring-slate-200 dark:bg-slate-800 dark:text-slate-300 dark:ring-slate-700"
      }`}
    >
      {value ? yes : no}
    </span>
  );
}

function SkeletonRows() {
  return (
    <div className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="space-y-3 p-4 sm:p-5 lg:p-6">
        {[...Array(5)].map((_, index) => (
          <div
            key={index}
            className="h-20 animate-pulse rounded-2xl bg-slate-100 dark:bg-slate-800"
          />
        ))}
      </div>
    </div>
  );
}

function EmptyState() {
  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-10 text-center">
        <div className="mx-auto flex h-16 w-16 items-center justify-center rounded-[24px] bg-indigo-50 text-indigo-700 dark:bg-indigo-500/10 dark:text-indigo-300">
          <FaImage />
        </div>

        <h3 className="mt-5 text-lg font-bold text-slate-900 dark:text-white">
          Aucun article
        </h3>

        <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
          Ajoutez un article ou modifiez les filtres du catalogue.
        </p>
      </div>
    </section>
  );
}

export default function ArticlesCatalogTable({
  articles,
  loading,
  canReorder,
  draggingId,
  dragOverId,
  deletingId,
  togglingId,
  reordering,
  onDragStart,
  onDragOver,
  onDragLeave,
  onDrop,
  onMoveUp,
  onMoveDown,
  onToggleActive,
  onEdit,
  onDelete,
  onImage,
}) {
  if (loading) return <SkeletonRows />;
  if (!articles.length) return <EmptyState />;

  return (
    <section className="overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-sm dark:border-slate-800 dark:bg-slate-900">
      <div className="h-1.5 bg-gradient-to-r from-[#013E8E] via-indigo-500 to-[#4CA82F]" />

      <div className="p-4 sm:p-5 lg:p-6">
        <div className="mb-4">
          <p className="text-[11px] uppercase tracking-[0.22em] text-slate-500 dark:text-slate-400">
            Catalogue
          </p>

          <h2 className="mt-1 text-xl font-bold text-slate-900 dark:text-white">
            Liste des articles
          </h2>

          <p className="mt-2 text-sm leading-6 text-slate-600 dark:text-slate-300">
            Tableau responsive avec défilement horizontal. Le glisser-déposer
            fonctionne uniquement sans filtre actif.
          </p>
        </div>

        <div className="overflow-x-auto rounded-[22px] border border-slate-200 dark:border-slate-800">
          <table className="min-w-[1180px] w-full">
            <thead className="bg-slate-50 text-xs uppercase tracking-[0.14em] text-slate-500 dark:bg-slate-950/70 dark:text-slate-400">
              <tr>
                <th className="px-4 py-3 text-left font-bold">Ordre</th>
                <th className="px-4 py-3 text-left font-bold">Article</th>
                <th className="px-4 py-3 text-left font-bold">Type</th>
                <th className="px-4 py-3 text-left font-bold">Prix</th>
                <th className="px-4 py-3 text-left font-bold">Taille</th>
                <th className="px-4 py-3 text-left font-bold">Stock</th>
                <th className="px-4 py-3 text-left font-bold">Catégorie</th>
                <th className="px-4 py-3 text-left font-bold">Boutique</th>
                <th className="sticky right-0 z-10 bg-slate-50 px-4 py-3 text-right font-bold dark:bg-slate-950">
                  Actions
                </th>
              </tr>
            </thead>

            <tbody className="divide-y divide-slate-100 text-sm dark:divide-slate-800">
              {articles.map((article) => {
                const imageUrl =
                  toAbsoluteArticlePhotoUrl(article.photo_url) ||
                  ARTICLE_FALLBACK_IMAGE;

                const isDragging = draggingId === article.id;
                const isDragOver = dragOverId === article.id;

                return (
                  <tr
                    key={article.id}
                    draggable={canReorder}
                    onDragStart={(event) => onDragStart(event, article)}
                    onDragOver={(event) => onDragOver(event, article)}
                    onDragLeave={onDragLeave}
                    onDrop={(event) => onDrop(event, article)}
                    className={`transition ${
                      canReorder ? "cursor-grab active:cursor-grabbing" : ""
                    } ${
                      isDragging
                        ? "opacity-60"
                        : "hover:bg-slate-50 dark:hover:bg-slate-800/50"
                    } ${
                      isDragOver
                        ? "bg-indigo-50 ring-2 ring-inset ring-indigo-400 dark:bg-indigo-500/10"
                        : ""
                    }`}
                  >
                    <td className="px-4 py-4 align-top">
                      <div className="flex items-center gap-2">
                        <span
                          className={`text-slate-400 ${
                            canReorder ? "cursor-grab" : "opacity-40"
                          }`}
                          title={
                            canReorder
                              ? "Glisser pour réordonner"
                              : "Désactivez les filtres pour réordonner"
                          }
                        >
                          <RxDragHandleDots2 />
                        </span>

                        <span className="min-w-8 rounded-xl bg-slate-100 px-2 py-1 text-center text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                          {article.ordre_article || "—"}
                        </span>

                        <div className="flex flex-col">
                          <button
                            type="button"
                            disabled={!canReorder || reordering}
                            onClick={() => onMoveUp(article)}
                            className="rounded-md p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-30 dark:hover:bg-slate-800 dark:hover:text-white"
                            title="Monter"
                          >
                            <FaArrowUp className="text-[11px]" />
                          </button>

                          <button
                            type="button"
                            disabled={!canReorder || reordering}
                            onClick={() => onMoveDown(article)}
                            className="rounded-md p-1 text-slate-500 transition hover:bg-slate-100 hover:text-slate-900 disabled:cursor-not-allowed disabled:opacity-30 dark:hover:bg-slate-800 dark:hover:text-white"
                            title="Descendre"
                          >
                            <FaArrowDown className="text-[11px]" />
                          </button>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-4 align-top">
                      <div className="flex items-start gap-3">
                        <div className="relative h-16 w-16 shrink-0 overflow-hidden rounded-2xl bg-slate-100 ring-1 ring-slate-200 dark:bg-slate-800 dark:ring-slate-700">
                          <img
                            src={imageUrl}
                            alt={article.nom || "Article"}
                            className="h-full w-full object-cover"
                            onError={(event) => {
                              event.currentTarget.src = ARTICLE_FALLBACK_IMAGE;
                            }}
                          />

                          <button
                            type="button"
                            onClick={() => onImage(article)}
                            className="absolute inset-x-1 bottom-1 rounded-lg bg-white/90 px-1 py-0.5 text-[10px] font-bold text-slate-700 shadow-sm transition hover:bg-white dark:bg-slate-900/90 dark:text-slate-200"
                          >
                            image
                          </button>
                        </div>

                        <div className="min-w-0">
                          <p className="font-bold text-slate-900 dark:text-white">
                            {article.nom || "Sans nom"}
                          </p>

                          <p className="mt-1 line-clamp-2 max-w-[420px] text-xs leading-5 text-slate-600 dark:text-slate-300">
                            {article.description || "Aucune description"}
                          </p>
                        </div>
                      </div>
                    </td>

                    <td className="px-4 py-4 align-top">
                      <span
                        className={`inline-flex rounded-full px-2.5 py-1 text-xs font-bold ring-1 ${
                          isLicenceCotisationArticle(article)
                            ? "bg-violet-50 text-violet-700 ring-violet-200 dark:bg-violet-500/10 dark:text-violet-300 dark:ring-violet-500/20"
                            : "bg-sky-50 text-sky-700 ring-sky-200 dark:bg-sky-500/10 dark:text-sky-300 dark:ring-sky-500/20"
                        }`}
                      >
                        {isLicenceCotisationArticle(article)
                          ? "Licence / cotisation"
                          : "Boutique"}
                      </span>
                    </td>

                    <td className="px-4 py-4 align-top font-bold text-slate-900 dark:text-white">
                      {formatEuro(article.prix)}
                    </td>

                    <td className="px-4 py-4 align-top text-slate-700 dark:text-slate-200">
                      {article.taille || "—"}
                    </td>

                    <td className="px-4 py-4 align-top">
                      <div className="space-y-1">
                        <BooleanBadge
                          value={!!article.is_stockable}
                          yes="Stockable"
                          no="Non stock"
                        />

                        <p
                          className={`text-xs font-bold ${
                            article.is_stockable &&
                            Number(article.quantite_stock || 0) <= 3
                              ? "text-amber-600 dark:text-amber-300"
                              : "text-slate-600 dark:text-slate-300"
                          }`}
                        >
                          Qté : {article.quantite_stock ?? 0}
                        </p>
                      </div>
                    </td>

                    <td className="px-4 py-4 align-top">
                      <span className="inline-flex rounded-full bg-slate-100 px-2.5 py-1 text-xs font-bold text-slate-700 dark:bg-slate-800 dark:text-slate-200">
                        {article.categorie || "—"}
                      </span>
                    </td>

                    <td className="px-4 py-4 align-top">
                      <StatusBadge
                        active={!!article.is_active}
                        disabled={togglingId === article.id}
                        onClick={() => onToggleActive(article)}
                      />
                    </td>

                    <td className="sticky right-0 z-10 bg-white px-4 py-4 align-top dark:bg-slate-900">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          type="button"
                          onClick={() => onImage(article)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-700 transition hover:bg-slate-50 dark:border-slate-700 dark:bg-slate-900 dark:text-slate-200 dark:hover:bg-slate-800"
                          title="Changer l’image"
                        >
                          <FaImage />
                        </button>

                        <button
                          type="button"
                          onClick={() => onEdit(article)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-indigo-200 bg-indigo-50 text-indigo-700 transition hover:bg-indigo-100 dark:border-indigo-500/20 dark:bg-indigo-500/10 dark:text-indigo-300"
                          title="Modifier"
                        >
                          <FaEdit />
                        </button>

                        <button
                          type="button"
                          disabled={deletingId === article.id}
                          onClick={() => onDelete(article)}
                          className="inline-flex h-9 w-9 items-center justify-center rounded-xl border border-rose-200 bg-rose-50 text-rose-700 transition hover:bg-rose-100 disabled:cursor-not-allowed disabled:opacity-50 dark:border-rose-500/20 dark:bg-rose-500/10 dark:text-rose-300"
                          title="Supprimer"
                        >
                          <FaTrash />
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </section>
  );
}